import { useMutation } from '@tanstack/react-query';
import { Col, Row } from 'antd';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import axiosGraphQL from 'apis/GraphQL/axiosGraphQL';
import { GET_ALL_CUSTOMER } from 'apis/GraphQL/Queries/customerQuery';
import { VARIABLE_GET_CUSTOMER } from 'apis/GraphQL/Variables/customer';
import { Button, ButtonFilter } from 'components';
import ButtonExport from 'components/Button/ButtonExport';
import { useTitle } from 'contexts/TitleProvider';
import { useUser } from 'contexts/UserProvider';
import useQueryParam from 'hooks/useQueryPrams';
import { IParamQraphQL } from 'interfaces/GraphQL/GraphQL';
import path from 'routes/Path';
import { DEFAULT_PAGE_SIZE } from 'utils/constants/constant';
import { exportExcel_v2 } from 'utils/constants/exportExcel';
import { icons } from 'utils/constants/icons';
import { Import } from 'assets/svg';
import { Dropdown, MenuProps } from 'antd';
import { UploadModal } from 'components/UploadModal_V2';
import FilterCustomerPage from './FilterCustomerPage';
import ModalAddCusPage from './ModalAddCusPage';
import TableCustomerPage from './TableCustomerPage';
import { ModalViewKycPage } from './ModalViewKycPage';
import { DownOutlined } from '@ant-design/icons';

const { PlusOutlined } = icons;

const CustomerPage = () => {
  const { user } = useUser();
  const navigate = useNavigate();
  const { setTitle, setBreadcrumb } = useTitle();

  const childRef = useRef<any>(null);
  const dataRef = useRef<any>(null);
  const firstRender = useRef<any>(true);

  const [edit, setEdit] = useState(-1);
  const [data, setData] = useState<any>([]);
  const [formParams, setFormParams] = useState<any>();
  const [selectedItem, setSelectedItem] = useState<any>({});

  const [isOpenModal, setIsOpenModal] = useState(false);
  const [isOpenFilter, setIsOpenFilter] = useState(false);

  const [isOpenImport, setIsOpenImport] = useState(false);
  const [importId, setImportId] = useState<string>('');
  const [templateFileName, setTemplateFileName] = useState<string>('');

  const [isOpenViewKycModal, setIsOpenViewKycModal] = useState(false);
  const [selectedKycCustomer, setSelectedKycCustomer] = useState<any>(null);

  const queryParam = useQueryParam();
  const page = parseInt(queryParam.get('page') + '') || 1;
  const page_size =
    parseInt(queryParam.get('page_size') + '') || DEFAULT_PAGE_SIZE;

  const mutationCustomer = useMutation({
    mutationKey: ['getAllCustomer'],
    mutationFn: (params: IParamQraphQL) =>
      axiosGraphQL.post('/graphql', params),
    onSuccess: (res, variables) => {
      if (variables.type === 'export') {
        const dataExport = res.data?.getCustomer?.items.map((item: any) => {
          const newItem = { ...item };
          Object.keys(newItem).forEach((key) => {
            if (key === 'typeSap') {
              if (newItem[key] === '1') newItem[key] = 'Cá nhân';
              else newItem[key] = 'Doanh nghiệp';
            }
            if (key === 'status') {
              if (newItem[key] === 'A100_01') newItem[key] = 'Hoạt động';
              else newItem[key] = 'Ngừng hoạt động';
            }
            if (key === 'titleSap') {
              if (newItem[key] === '0002') newItem[key] = 'Nam';
              else if (newItem[key] === '0001') newItem[key] = 'Nữ';
              else newItem[key] = 'Công ty';
            }
          });
          return newItem;
        });
        return exportExcel_v2(variables.headerRow, dataExport, 'KhachHang');
      }

      dataRef.current = res.data.getCustomer;
      setData(res.data.getCustomer);
    },
    onError: (error) => {
      console.log(error);
    },
  });

  const refetch = useCallback(
    (toFirstPage?: boolean, type?: any, headerRow?: any) => {
      mutationCustomer.mutate({
        query: GET_ALL_CUSTOMER,
        variables: VARIABLE_GET_CUSTOMER({
          page: toFirstPage ? 1 : page,
          page_size:
            type === 'export'
              ? (dataRef && dataRef?.current?.pageInfo.totalItems) || null
              : page_size,
          ...(!(user?.isAdmin || user?.isHO) && { plantId: user?.plantCode }),
          ...(formParams && formParams),
        }),
        ...(type && { type }),
        ...(headerRow && { headerRow }),
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [formParams, page, page_size],
  );

  useEffect(() => {
    firstRender.current || navigate(`?page=${page}&page_size=${page_size}`);
  }, [navigate, page, page_size]);

  useEffect(() => {
    firstRender.current || navigate(`?page=1&page_size=${page_size}`);
    firstRender.current = false;
  }, [formParams]);

  useEffect(() => {
    setTitle('Khách hàng');
    setBreadcrumb([
      { title: 'Kế hoạch', href: path.HO_PLAN },
      { title: 'Khách hàng' },
    ]);
  }, [setBreadcrumb, setTitle, user]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  const handleExportExcel = async () => {
    if (childRef && childRef.current) {
      childRef.current.handleExportExcel();
    }
  };

  const handleAddCustomer = () => {
    refetch();
    setIsOpenModal(false);
  };

  const handleViewForm = (record: any) => {
    setSelectedKycCustomer(record);
    setIsOpenViewKycModal(true);
  };

  const handleEditCustomer = (record: any) => {
    setSelectedItem(record);
    setIsOpenModal(true);
  };

  const handleCreateCustomer = () => {
    setSelectedItem({});
    setIsOpenModal(true);
  };

  const handleModalCancel = () => {
    setIsOpenModal(false);
    setSelectedItem({});
  };

  const importItems: MenuProps['items'] = [
    {
      key: 'IMP_DATA_KYC_CN',
      label: 'Import Cá Nhân',
      onClick: () => {
        setImportId('IMP_DATA_KYC_CN');
        setTemplateFileName('template_kh_ca_nhan.xlsx');
        setIsOpenImport(true);
      },
    },
    {
      key: 'IMP_DATA_KYC_DN',
      label: 'Import Doanh Nghiệp',
      onClick: () => {
        setImportId('IMP_DATA_KYC_DN');
        setTemplateFileName('template_kh_doanh_nghiep.xlsx');
        setIsOpenImport(true);
      },
    },
  ];

  return (
    <>
      <Row gutter={[8, 16]} align={'middle'} justify={'end'}>
        <Col>
          <ButtonFilter isOpen={isOpenFilter} setIsOpen={setIsOpenFilter} />
        </Col>
        <Col>
          <Dropdown
            menu={{ items: importItems }}
            trigger={['click']}
            placement="bottomRight"
          >
            <Button
              title={
                <span className="flex items-center gap-1">
                  Import <DownOutlined style={{ fontSize: '12px' }} />
                </span>
              }
              iconBefore={<Import />}
              customClass="!bg-white !text-main !border-main !font-normal"
            />
          </Dropdown>
        </Col>
        <Col>
          <ButtonExport onClick={handleExportExcel} />
        </Col>
        {!user?.isHO && (
          <Col>
            <Button
              fill
              title="Tạo"
              iconBefore={<PlusOutlined />}
              handleOnclick={handleCreateCustomer}
            />
          </Col>
        )}
      </Row>
      <FilterCustomerPage
        isOpen={isOpenFilter}
        setOpen={setIsOpenFilter}
        setFormParams={setFormParams}
      />
      <ModalAddCusPage
        data={selectedItem}
        isOpenModal={isOpenModal}
        refetch={refetch}
        onCancel={handleModalCancel}
        onSubmit={handleAddCustomer}
        setIsOpenModal={setIsOpenModal}
      />

      <TableCustomerPage
        data={data}
        edit={edit}
        ref={childRef}
        setEdit={setEdit}
        refetch={refetch}
        onEdit={handleEditCustomer}
        onViewForm={handleViewForm}
        loading={mutationCustomer.isPending}
      />
      <UploadModal
        title="Import"
        open={isOpenImport}
        setOpen={setIsOpenImport}
        importId={importId}
        templateFileName={templateFileName}
        refetch={refetch}
        okTitle="Gửi đề xuất"
      />
      <ModalViewKycPage
        isOpen={isOpenViewKycModal}
        onClose={() => setIsOpenViewKycModal(false)}
        sapCustomerId={
          selectedKycCustomer?.sapCustomerId || selectedKycCustomer?.id
        }
        typeSap={selectedKycCustomer?.typeSap}
        onSuccess={refetch}
      />
    </>
  );
};

export default CustomerPage;
